import React, { useEffect } from "react";
import { Button } from "@/app/global-components/button";
import {
  Link,
  Link as LinkTo,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { cn, getData } from "@/lib/utils";
import { useDispatch } from "react-redux";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { FaCog, FaInfo } from "react-icons/fa";
import { updateAuthUser } from "@/store/authSlice";
import { Avatar } from "@mui/material";
import { IoExit } from "react-icons/io5";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG, ROLE } from "@/types";
import { routes } from "@/consts";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";

interface SidebarProps {
  sidebarOpen: boolean;
  isLogging?: boolean;
  list?: { title: string; path: string; icon: JSX.Element; header: string }[];
  marketPlaceList?: {
    title: string;
    path: string;
    icon: JSX.Element;
    header: string;
  }[];
  toggleSidebar: () => void;
}

const SidebarView: React.FC<SidebarProps> = ({
  sidebarOpen,
  list,
  marketPlaceList,
  toggleSidebar,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const dispatch = useDispatch();
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const navigate = useNavigate();
  const location = useLocation();
  const accesToken = getData("tokens")?.accessToken;

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [sidebarOpen]);

  return (
    <nav
      className={cn(
        `transition-transform duration-300 md:hidden ease-in-out fixed z-[9999] top-0 left-0 w-[85%] h-[calc(100vh-50px)] bg-white shadow-2xl flex flex-col`,
        { "translate-x-full": !sidebarOpen && locale === "ar" },
        { "translate-x-[-100%]": !sidebarOpen && locale === "en" },
        locale === LANG.AR
          ? "right-0 border-l border-gray-200"
          : "left-0 border-r border-gray-200"
      )}
    >
      <div className="border-b border-gray-200 flex-shrink-0">
        <div className="flex justify-between items-center p-6">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">
              Med<span>ex</span>
            </h1>
          </div>
          <Button
            onClick={toggleSidebar}
            className="text-gray-600 hover:text-gray-800 transition-colors duration-200 p-2 rounded-lg hover:bg-gray-100"
          >
            {locale === LANG.AR ? (
              <KeyboardDoubleArrowRightIcon className="text-2xl" />
            ) : (
              <KeyboardDoubleArrowLeftIcon className="text-2xl" />
            )}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-4">
        {/* Navigation Menu */}
        <div className="p-4 space-y-2">
          {/* Main Navigation */}
          <div className="space-y-1">
            {list?.map((item) => (
              <LinkTo to={item.path} key={item.title}>
                <div
                  className={cn(
                    "flex items-center gap-3 cursor-pointer px-4 py-3 text-base font-medium rounded-xl transition-all duration-200",
                    {
                      "bg-gradient-to-r from-purple-50 to-blue-50 text-white shadow-lg":
                        location.pathname == item.path,
                      "text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 hover:text-purple-700":
                        location.pathname != item.path,
                    }
                  )}
                >
                  <div
                    className={cn("text-xl transition-all duration-200", {
                      "text-white": location.pathname == item.path,
                      "text-gray-500": location.pathname != item.path,
                    })}
                  >
                    {item.icon}
                  </div>
                  <span>{item.title}</span>
                </div>
              </LinkTo>
            ))}
          </div>

          {/* Divider */}
          <div className="my-6">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>

          {/* Marketplace Navigation */}
          {!!accesToken && (
            <div className="space-y-1">
              {marketPlaceList?.map((item) => (
                <LinkTo to={item.path} key={item.title}>
                  <div
                    className={cn(
                      "flex items-center gap-3 cursor-pointer px-4 py-3 text-base font-medium rounded-xl transition-all duration-200",
                      {
                        "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg":
                          location.pathname == item.path,
                        "text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-700":
                          location.pathname != item.path,
                      }
                    )}
                  >
                    <div
                      className={cn("text-xl transition-all duration-200", {
                        "text-white": location.pathname == item.path,
                        "text-gray-500": location.pathname != item.path,
                      })}
                    >
                      {item.icon}
                    </div>
                    <span>{item.title}</span>
                  </div>
                </LinkTo>
              ))}
            </div>
          )}

          {/* Settings & Help */}
          {!!accesToken && (
            <div className="space-y-1 mt-6">
              <LinkTo to={routes.settings}>
                <div
                  className={cn(
                    "flex items-center gap-3 cursor-pointer px-4 py-3 text-base font-medium rounded-xl transition-all duration-200",
                    {
                      "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg":
                        location.pathname == routes.settings,
                      "text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-green-700":
                        location.pathname != routes.settings,
                    }
                  )}
                >
                  <div
                    className={cn("text-xl transition-all duration-200", {
                      "text-white": location.pathname == routes.settings,
                      "text-gray-500": location.pathname != routes.settings,
                    })}
                  >
                    <FaCog />
                  </div>
                  <span>{t("navigation.Settings")}</span>
                </div>
              </LinkTo>

              <LinkTo to={routes.info}>
                <div
                  className={cn(
                    "flex items-center gap-3 cursor-pointer px-4 py-3 text-base font-medium rounded-xl transition-all duration-200",
                    {
                      "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg":
                        location.pathname == routes.info,
                      "text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 hover:text-orange-700":
                        location.pathname != routes.info,
                    }
                  )}
                >
                  <div
                    className={cn("text-xl transition-all duration-200", {
                      "text-white": location.pathname == routes.info,
                      "text-gray-500": location.pathname != routes.info,
                    })}
                  >
                    <FaInfo />
                  </div>
                  <span>{t("navigation.Help")}</span>
                </div>
              </LinkTo>
            </div>
          )}

          {/* Beta Badge */}
          <div className="mt-8 px-4">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-semibold px-3 py-2 rounded-full shadow-lg flex items-center justify-center gap-2">
              <RocketLaunchIcon fontSize="small" className="animate-pulse" />
              <span>{t("general.betaLaunch")}</span>
            </div>
          </div>
        </div>
      </div>

      {!accesToken && (
        <div className="p-6 space-y-4 flex-shrink-0 border-t border-gray-200 bg-white">
          <LinkTo
            onClick={toggleSidebar}
            to={routes.login}
            state={{ role: ROLE.BUYER }}
            className="block w-full px-6 py-4 text-center text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 font-medium text-lg"
          >
            {t("authentication.login")}
          </LinkTo>

          <Link
            to={routes.register}
            className="block w-full px-6 py-4 text-center text-white bg-gradient-to-r bg-zinc-900 rounded-xl transition-all duration-200 font-medium text-lg shadow-lg hover:shadow-xl"
          >
            {t("actions.startNow")}
          </Link>
        </div>
      )}

      {authUser && (
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0 mt-auto">
          <div className="flex items-center justify-between p-3 bg-white rounded-xl shadow-sm">
            <Link
              to={routes.settings}
              className="flex items-center gap-3 flex-1"
            >
              <Avatar
                sx={{
                  bgcolor: "linear-gradient(45deg, #667eea 0%, #764ba2 100%)",
                  width: 40,
                  height: 40,
                  fontSize: "1rem",
                  fontWeight: "bold",
                }}
              >
                {authUser.nickName[0]}
              </Avatar>
              <div className="flex flex-col min-w-0">
                <span className="font-semibold text-gray-900 text-sm truncate">
                  {authUser?.nickName}
                </span>
                <span className="text-xs text-gray-500 truncate max-w-[150px]">
                  {authUser?.email}
                </span>
              </div>
            </Link>
            <Button
              onClick={() => {
                dispatch(updateAuthUser(null));
                localStorage.removeItem("tokens");
                localStorage.removeItem("userInfo");
                navigate(routes.home);
              }}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-all duration-200"
            >
              <IoExit className="text-xl" />
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default SidebarView;
